// Client estimate page
import { CUR, baseRates, ccyHeader, grandTotal } from '../core/budget.js';
import { useEffect, useMemo, useState } from '../core/react.js';
import { loadJsPDF } from '../core/excel.js';
import { MONO, T, fmt } from '../core/theme.js';
import { Icon } from '../ui/icons.js';
import { FitEstimate, buildEstimate, buildChangeOrder, downloadEstimatePDF, estMoney } from './estimate-pdf.js';
import { useApprovals, signed } from './approvals.js';
import { partsLabel } from '../core/approvals.js';
import { pageHints } from '../core/page-hints.js';
import { SignatureControl, useMySignature } from '../ui/signature.js';
import { plural, latin } from '../core/format.js';
import { InternalPreviewSheet, buildInternalPDF, exportInternalXlsx } from './internal-pl.js';
import { IssuedEstimates } from './settings.js';
import { audit } from '../core/audit.js';
import { isoToday } from '../core/dates.js';

// VAT line and payment terms for client estimates, kept with the project
const DEFAULT_TERMS = 'Payment terms: 50% on confirmation, balance within 30 days of the event. This estimate is valid for 30 days. Final costs may vary with changes to the scope.';
function EstimateTaxTerms({ project, updateProject, readOnly }) {
  if (!project) return null;
  const vat = project.estVat || { on: false, rate: 20, label: 'VAT' };
  const setVat = patch => updateProject({ estVat: { ...vat, ...patch } });
  const small = { ...estInputSmall };
  return React.createElement("div", { style: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 } },
    React.createElement("label", { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.slate } },
      React.createElement("input", { type: 'checkbox', disabled: readOnly, checked: !!vat.on, onChange: e => setVat({ on: e.target.checked }) }), ' Add tax to the total'),
    vat.on && React.createElement("div", { style: { display: 'flex', gap: 8 } },
      React.createElement("input", { value: vat.label || '', disabled: readOnly, placeholder: 'VAT', onChange: e => setVat({ label: e.target.value }), style: { ...small, flex: 1 } }),
      React.createElement("input", { type: 'number', min: 0, step: 0.5, value: vat.rate ?? '', disabled: readOnly, onChange: e => setVat({ rate: e.target.value === '' ? '' : Number(e.target.value) }), style: { ...small, width: 70 } }),
      React.createElement("span", { style: { fontSize: 12, color: T.slate, alignSelf: 'center' } }, '%')),
    React.createElement("textarea", { value: project.estTerms ?? '', disabled: readOnly, rows: 4, placeholder: 'Payment terms and conditions (printed under the total)', onChange: e => updateProject({ estTerms: e.target.value }), style: { ...small, resize: 'vertical', fontFamily: 'inherit' } }),
    !project.estTerms && !readOnly && React.createElement("button", { onClick: () => updateProject({ estTerms: DEFAULT_TERMS }), style: { alignSelf: 'flex-start', fontSize: 11, color: T.navyL, textDecoration: 'underline' } }, 'Insert standard terms'));
}
const estInputSmall = { padding: '7px 9px', border: `1px solid ${T.rule}`, background: T.ivory, fontSize: 12, color: T.coal, borderRadius: 0 };
export function PDFPreview({
  project,
  sections,
  fx,
  user
,
  updateProject
}) {
  // "Prepare a change order" on Approvals & POs opens the page at the change order
  const [tmpl, setTmpl] = useState(() => { const c = pageHints.changeOrder; pageHints.changeOrder = false; return c ? 'change' : 'client'; });
  const entity = 'glassbox';   // the issuing company (only one today)
  const [generated, setGen] = useState(false);
  // the PDF library measures text for the layout, so the preview waits for it and is drawn again once it's here
  const [pdfReady, setPdfReady] = useState(!!(window.jspdf && window.GB_ASSETS));
  useEffect(() => { if (!pdfReady) loadJsPDF().then(() => setPdfReady(true)).catch(() => {}); }, []);
  const gt = useMemo(() => grandTotal(sections, fx), [sections, fx]);
  const internal = tmpl === 'internal';
  const [estDate, setEstDate] = useState(() => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  });
  const [recipient, setRecipient] = useState(project?.client || '');
  const [estTitle, setEstTitle] = useState(project?.name || '');
  const [signedBy, setSignedBy] = useState(user?.name || '');
  const [withSig, setWithSig] = useState(true);
  // your own uploaded signature, or the built-in one if you are the signer set on the server; any name can go under it
  const [mySig, setMySig] = useMySignature();
  const sigToUse = withSig ? (mySig || (user?.canSign ? true : null)) : null;
  const estLayout = useMemo(() => buildEstimate({
    sections,
    fx,
    meta: {
      entity,
      date: estDate,
      recipient,
      title: estTitle,
      signedBy,
      signature: sigToUse,
      headings: {},
      vat: project?.estVat || null,
      terms: project?.estTerms || ''
    }
  }), [sections, fx, entity, estDate, recipient, estTitle, signedBy, sigToUse, project?.estVat, project?.estTerms, pdfReady]);
  // a change order: what changed since the client last approved (pages/approvals.js), as a letter of its own
  const change = tmpl === 'change';
  const appr = useApprovals(project, sections, fx);
  const base = appr && !appr.ccyChanged ? appr.base : null;
  const coLayout = useMemo(() => {
    if (!change || !base || !appr.changes.length) return null;
    const last = base.parts[base.parts.length - 1];
    const ap = (project?.approvals || []).find(x => x.estimateId === last.id);
    const po = (project?.pos || []).filter(p => base.parts.some(d => d.id === p.covers)).map(p => p.number).join(', ');
    return buildChangeOrder({ changes: appr.changes, base: { total: base.total, label: partsLabel(base), date: ap?.date, po },
      meta: { entity, date: estDate, recipient, title: estTitle, signedBy, signature: sigToUse, vat: project?.estVat || null } });
  }, [change, appr, base, entity, estDate, recipient, estTitle, signedBy, sigToUse, project?.estVat, project?.approvals, project?.pos, pdfReady]);
  const coCount = (appr?.docs || []).filter(d => d.kind === 'change').length;
  const letter = change ? coLayout : estLayout;
  const estInput = {
    width: '100%',
    padding: '8px 10px',
    border: `1px solid ${T.rule}`,
    background: T.ivory,
    fontSize: 12,
    color: T.coal,
    borderRadius: 0
  };
  return React.createElement("div", {
    style: {
      display: 'flex',
      height: '100%',
      overflow: 'hidden',
      background: T.ivoryD
    },
    className: "fade-up pane-row"
  }, React.createElement("div", {
    style: {
      width: 268,
      background: T.white,
      borderRight: `1px solid ${T.rule}`,
      padding: '26px 22px',
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      flexShrink: 0,
      overflowY: 'auto'
    }
  }, React.createElement("div", null, React.createElement("div", {
    style: {
      fontSize: 16,
      fontWeight: 600,
      color: T.coal,
      marginBottom: 4
    }
  }, "Generate PDF"), React.createElement("div", {
    style: {
      fontSize: 12,
      color: T.mist
    }
  }, "Branded estimate or internal P&L")), React.createElement("div", null, React.createElement("div", {
    style: {
      fontSize: 9.5,
      fontWeight: 700,
      letterSpacing: '0.1em',
      color: T.mist,
      marginBottom: 10
    }
  }, "TEMPLATE"), [{
    id: 'client',
    label: 'Client Estimate',
    get sub() { return 'Letter on Glassbox letterhead · ' + ccyHeader(CUR.bill); }
  }, {
    id: 'change',
    label: 'Change order',
    sub: !appr ? 'Loading…' : base ? `Changes since ${partsLabel(base)} was approved` : 'Mark an estimate approved first (Approvals & POs)',
    off: !base
  }, {
    id: 'internal',
    label: 'Internal P&L',
    sub: 'Full budget · Confidential'
  }].map(t => React.createElement("button", {
    key: t.id,
    type: 'button',
    'aria-pressed': tmpl === t.id,
    disabled: !!t.off && tmpl !== t.id,
    onClick: () => setTmpl(t.id),
    style: {
      display: 'block',
      width: '100%',
      textAlign: 'left',
      padding: '12px 14px',
      marginBottom: 10,
      cursor: t.off ? 'default' : 'pointer',
      opacity: t.off && tmpl !== t.id ? 0.6 : 1,
      transition: 'border-color .15s',
      border: `1.5px solid ${tmpl === t.id ? T.navy : T.rule}`,
      background: tmpl === t.id ? T.ivory : T.white
    }
  }, React.createElement("div", {
    style: {
      fontSize: 13,
      fontWeight: 600,
      color: T.coal,
      marginBottom: 3
    }
  }, t.label), React.createElement("div", {
    style: {
      fontSize: 11,
      color: T.mist
    }
  }, t.sub)))), !internal && React.createElement("div", {
    style: {
      display: 'flex',
      flexDirection: 'column',
      gap: 10
    }
  }, React.createElement("div", {
    style: {
      fontSize: 9.5,
      fontWeight: 700,
      letterSpacing: '0.1em',
      color: T.mist
    }
  }, "LETTER"), React.createElement("input", {
    type: "date",
    value: estDate,
    onChange: e => setEstDate(e.target.value),
    style: estInput
  }), React.createElement("textarea", {
    value: recipient,
    onChange: e => setRecipient(e.target.value),
    rows: 5,
    placeholder: 'Recipient name\nCompany\nAddress lines…',
    style: {
      ...estInput,
      resize: 'vertical',
      lineHeight: 1.45
    }
  }), React.createElement("input", {
    value: estTitle,
    onChange: e => setEstTitle(e.target.value),
    placeholder: "Estimate title, e.g. Sibos Media Reception",
    style: estInput
  }), React.createElement("input", {
    value: signedBy,
    onChange: e => setSignedBy(e.target.value),
    placeholder: "Signed by",
    style: estInput
  }), React.createElement(SignatureControl, {
    sig: mySig, setSig: setMySig, builtIn: !!user?.canSign, on: withSig, setOn: setWithSig,
    readOnly: user?.role === 'Read-only', flash: m => window.alert(m)
  }), React.createElement(EstimateTaxTerms, {
    project,
    updateProject,
    readOnly: user?.role === 'Read-only'
  }), letter && letter.pages.length > 1 && React.createElement("div", {
    style: {
      fontSize: 11,
      color: T.mist
    }
  }, letter.pages.length, " pages"), letter && letter.missing.length > 0 && React.createElement("div", {
    style: {
      fontSize: 11,
      color: T.amber,
      lineHeight: 1.45
    }
  }, "These characters can't be printed in the PDF and will be left blank: ", letter.missing.join(' '))), React.createElement("div", null, React.createElement("div", {
    style: {
      fontSize: 9.5,
      fontWeight: 700,
      letterSpacing: '0.1em',
      color: T.mist,
      marginBottom: 10
    }
  }, "DOCUMENT SUMMARY"), (change ? [[base ? estMoney(base.total) : '—', 'Approved (' + CUR.bill + ')'], [coLayout ? signed(coLayout.total, CUR.bill) : '—', 'This change order'], [coLayout ? estMoney(coLayout.newTotal) : '—', 'Revised total'], [plural(appr?.changes.length || 0, 'change'), 'Lines']] : [internal ? [fmt.usd(gt.outUSD), 'Total Out-Cost (' + CUR.bill + ')'] : [estMoney(estLayout.total), 'Estimate total (' + CUR.bill + ')'], ...(internal ? [[fmt.gbp(gt.inGBP), 'Total In-Cost (' + CUR.cost + ')'], [fmt.usd(gt.margUSD), 'Projected margin (' + CUR.bill + ')']] : []), [sections.flatMap(s => s.items).length + ' line items', 'Line Items'], [sections.length + ' sections', 'Sections']]).map(([v, l]) => React.createElement("div", {
    key: l,
    style: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 0',
      borderBottom: `1px solid ${T.rule}`
    }
  }, React.createElement("span", {
    style: {
      fontSize: 12,
      color: T.mist
    }
  }, l), React.createElement("span", {
    style: {
      ...MONO,
      fontSize: 12,
      fontWeight: 500,
      color: T.coal
    }
  }, v)))), React.createElement("button", {
    onClick: async () => {
      if (change) {
        if (!coLayout) return;
        if (gt.missing && gt.missing.length) {
          window.alert(`Some lines are in ${gt.missing.join(', ')}, which has no exchange rate, so they would be missing from the change order. Add the rate in Settings (or in this project's locked rates) first.`);
          return;
        }
        try {
          const label = window.prompt('Name this change order so you can find it later', `CO${coCount + 1}`);
          if (label == null) return;
          const name = label.trim() || `CO${coCount + 1}`;
          const safe = latin(estTitle || project?.name || 'Estimate').replace(/[^a-zA-Z0-9_-]+/g, '-');
          const filename = `${safe}-Change-order-${latin(name).replace(/[^a-zA-Z0-9_-]+/g, '-')}-${estDate}.pdf`;
          downloadEstimatePDF(coLayout, filename);
          await window.PrismAPI.call('estimate.save', {
            projectId: project.id, label: name,
            data: {
              kind: 'change', layout: coLayout, changes: coLayout.changes, total: coLayout.total, baseTotal: coLayout.baseTotal, newTotal: coLayout.newTotal,
              base: base.parts.map(d => d.id), ccy: coLayout.ccy, filename, meta: { title: estTitle, recipient, date: estDate, entity, signedBy },
              rates: baseRates(project), costCcy: CUR.cost, billCcy: CUR.bill
            }
          }).then(() => window.dispatchEvent(new Event('prism-estimate-saved'))).catch(err => window.alert('The PDF downloaded, but it could not be saved in Prism: ' + err.message));
          setGen('done');
          setTimeout(() => setGen(false), 4000);
        } catch (err) {
          console.error('PDF error:', err);
          setGen('error');
          setTimeout(() => setGen(false), 4000);
        }
        return;
      }
      if (!internal) {
        try {
          if (gt.missing && gt.missing.length) {
            window.alert(`Some lines are in ${gt.missing.join(', ')}, which has no exchange rate, so they would be missing from the estimate. Add the rate in Settings (or in this project's locked rates) first.`);
            return;
          }
          const count = Number(project?.estimateCount || 0) + 1;
          const label = window.prompt('Name this version so you can find it later (for example "v3 sent to procurement")', `v${count}`);
          if (label == null) return;
          const safe = latin(estTitle || project?.name || 'Estimate').replace(/[^a-zA-Z0-9_-]+/g, '-');
          const filename = `${safe}-Estimate-${estDate}.pdf`;
          // Issuing an estimate locks the project's exchange rates, so its prices can't shift afterwards.
          const lock = !project?.fxLocked ? {
            fxLocked: true,
            fx: JSON.parse(JSON.stringify(baseRates(project))),
            fxLockedAt: isoToday(),
            fxLockedBy: user?.name || ''
          } : {};
          updateProject({ ...lock, estimateCount: count });
          downloadEstimatePDF(estLayout, filename);
          window.PrismAPI.call('estimate.save', {
            projectId: project.id,
            label: label.trim() || `v${count}`,
            data: {
              layout: estLayout, lines: estLayout.lines, total: estLayout.total, ccy: estLayout.ccy, filename,
              meta: { title: estTitle, recipient, date: estDate, entity, signedBy },
              rates: baseRates(project), costCcy: CUR.cost, billCcy: CUR.bill
            }
          }).then(() => window.dispatchEvent(new Event('prism-estimate-saved'))).catch(err => window.alert('The PDF downloaded, but it could not be saved in Prism: ' + err.message));
          setGen('done');
          setTimeout(() => setGen(false), 4000);
        } catch (err) {
          console.error('PDF error:', err);
          setGen('error');
          setTimeout(() => setGen(false), 4000);
        }
        return;
      }
      setGen('generating');
      try {
        const pdf = buildInternalPDF({ project, sections, fx, user });
        const safeName = latin(project?.name || 'Budget').replace(/[^a-zA-Z0-9_-]/g, '-');
        const filename = `${safeName}-Internal-PnL-${isoToday()}.pdf`;
        pdf.save(filename);
        audit('pnl.pdf', { project: project?.name });
        setGen('done');
        setTimeout(() => setGen(false), 4000);
      } catch (err) {
        console.error('PDF error:', err);
        setGen('error');
        setTimeout(() => setGen(false), 4000);
      }
    },
    disabled: generated === 'generating' || !pdfReady || (change && !coLayout),
    style: {
      padding: '12px',
      background: generated === 'generating' ? T.slate : T.navy,
      color: T.white,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.1em',
      border: 'none',
      cursor: generated === 'generating' ? 'wait' : 'pointer',
      transition: 'filter .15s'
    },
    onMouseEnter: e => {
      if (generated !== 'generating') e.target.style.filter = 'brightness(1.1)';
    },
    onMouseLeave: e => e.target.style.filter = ''
  }, !pdfReady ? 'Preparing…' : generated === 'generating' ? '⏳  Generating PDF…' : '↧  DOWNLOAD PDF'), internal && React.createElement("button", {
    onClick: async () => {
      try { await exportInternalXlsx({ project, sections, fx, user }); }
      catch (err) { console.error(err); window.alert('The Excel file could not be made: ' + (err.message || err)); }
    },
    style: {
      marginTop: 8,
      width: '100%',
      padding: '11px',
      background: T.white,
      color: T.navy,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.1em',
      border: `1px solid ${T.navy}`,
      cursor: 'pointer'
    }
  }, '↧  DOWNLOAD EXCEL'), !internal && gt.missing && gt.missing.length > 0 && React.createElement("div", {
    style: { marginTop: 8, fontSize: 11, color: T.red }
  }, `No exchange rate for ${gt.missing.join(', ')}. Add it before issuing.`), !internal && project && !project.fxLocked && React.createElement("div", {
    style: { marginTop: 8, fontSize: 11, color: T.slate }
  }, "Downloading locks this project's exchange rates, so the prices in this estimate can't change later."), !internal && project && React.createElement(IssuedEstimates, {
    project,
    current: estLayout,
    status: appr?.status,
    flash: m => window.alert(m)
  }), generated === 'done' && React.createElement("div", {
    style: {
      textAlign: 'center',
      fontSize: 12,
      color: T.green,
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6
    }
  }, React.createElement(Icon.check, {
    s: 13
  }), " Downloaded successfully"), generated === 'error' && React.createElement("div", {
    style: {
      textAlign: 'center',
      fontSize: 12,
      color: T.red,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6
    }
  }, "\u26A0 PDF generation failed \u2014 try again"), React.createElement("div", {
    style: {
      marginTop: 'auto',
      padding: '14px',
      background: internal ? T.redBg : T.greenBg,
      border: `1px solid ${internal ? T.redBr : T.greenBr}`
    }
  }, React.createElement("div", {
    style: {
      fontSize: 10,
      fontWeight: 700,
      color: internal ? T.red : T.green,
      letterSpacing: '0.06em',
      marginBottom: 4
    }
  }, internal ? '⚠  CONFIDENTIAL' : '✓  CLIENT-SAFE'), React.createElement("div", {
    style: {
      fontSize: 11,
      color: internal ? T.red : T.green
    }
  }, internal ? 'Contains margin & in-cost data. Not for client distribution.' : 'Out-cost only. Safe for client distribution.'))), !internal ? React.createElement("div", {
    style: {
      flex: 1,
      overflowY: 'auto',
      padding: '32px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }
  }, letter ? React.createElement(FitEstimate, {
    layout: letter
  }) : React.createElement("div", {
    style: { maxWidth: 420, marginTop: 40, fontSize: 13, color: T.slate, lineHeight: 1.6, textAlign: 'center' }
  }, !appr ? 'Loading…' : !base ? 'A change order lists what changed since the client approved an estimate. Mark the estimate approved on Approvals & POs first.' : 'The budget matches what the client approved, so there is nothing to put in a change order.')) : React.createElement("div", {
    style: { flex: 1, minWidth: 0, display: 'flex', background: T.ivoryD }
  }, React.createElement(InternalPreviewSheet, { project, sections, fx, user })));
}
